import { Model } from '@/models/Model';
import { QueryClient, QueryKey } from '@tanstack/react-query';
import { CacheStrategy, AddOptions, UpdateOptions } from '../CacheStrategy';

export abstract class ListCacheStrategy<T extends Model>
  implements CacheStrategy<T>
{
  constructor(
    protected queryClient: QueryClient,
    protected queryKey: QueryKey,
  ) {}
  getRawData<T>() {
    const listParents = this.queryClient.getQueryData<T>(this.queryKey);
    return listParents;
  }

  add(data: T, options?: AddOptions): void {
    const { position } = options || {};
    const previousData = this.get() || [];
    if (position === 'start') {
      const newData = [data, ...previousData];
      this.set(newData);
    } else {
      const newData = [...previousData, data];
      this.set(newData);
    }
  }

  update(data: Partial<T> & Model, options?: UpdateOptions): void {
    const { newId } = options || {};
    const previousData = this.get();
    if (previousData) {
      const updatedData = previousData.map(item =>
        item.id === data.id ? { ...item, ...data, id: newId || item.id } : item,
      );
      this.set(updatedData);
    }
  }

  abstract get(): T[] | undefined;

  abstract set(data: T[]): void;

  abstract setIfAbsent(data: T[]): void;

  abstract getById(id: number): T | undefined;

  merge(data: T[]): void {
    const previousData = this.get();
    if (previousData) {
      const updatedData = [...previousData, ...data];
      this.set(updatedData);
    }
  }

  remove(id: number): void {
    const previousData = this.get();
    if (previousData) {
      const updatedData = [...previousData].filter(item => item.id !== id);
      this.set(updatedData);
    }
  }
}
